/**
 * File: useEffortQueries.ts
 * Path: apps/admin/src/shared/hooks/useEffortQueries.ts
 * Description: Time and costs for one lead or client, the person's own
 * hourly rate, and the writes (React Query over effortRepo).
 */

import { useQuery } from "@tanstack/react-query";
import {
  summariseEffort,
  type CostEntry,
  type EffortRecordKind,
  type EffortSummary,
  type TimeEntry,
} from "@idk/config";
import {
  effortRepo,
  type EffortRecord,
} from "../services/effortService";

export const effortKey = ["effort"] as const;

export interface EffortOf {
  time: TimeEntry[];
  costs: CostEntry[];
  summary: EffortSummary;
  isLoading: boolean;
  error: unknown;
}

/**
 * Everything logged against a record. A client that started as a lead (same
 * id) also carries what was spent winning them, so pass `alsoLeadId`.
 */
export const useEffort = (kind: EffortRecordKind, id: string, alsoLeadId?: string): EffortOf => {
  const own = useQuery({
    queryKey: [...effortKey, kind, id],
    queryFn: async () => ({ time: await effortRepo.time(kind, id), costs: await effortRepo.costs(kind, id) }),
  });
  const lead = useQuery({
    queryKey: [...effortKey, "lead", alsoLeadId ?? ""],
    queryFn: async () => ({
      time: await effortRepo.time("lead", alsoLeadId as string),
      costs: await effortRepo.costs("lead", alsoLeadId as string),
    }),
    enabled: Boolean(alsoLeadId) && !(kind === "lead" && alsoLeadId === id),
  });
  const time = [...(own.data?.time ?? []), ...(lead.data?.time ?? [])];
  const costs = [...(own.data?.costs ?? []), ...(lead.data?.costs ?? [])];
  return {
    time,
    costs,
    summary: summariseEffort(time, costs),
    isLoading: own.isLoading,
    error: own.error ?? lead.error,
  };
};
export type { EffortRecord };
